/**
 * LoginShell — the chrome both login screens share.
 *
 * The local form and the Advisory landing page differ only in what goes inside
 * the panel. Everything around it — backdrop, panel, product lockup, title,
 * "Welcome to <product>" and the legal notice — is identical, so it lives here
 * once. Two copies would drift: a lockup fix or a copy change made to one screen
 * and forgotten on the other.
 *
 * It reads the variant itself rather than taking it as props, so a screen never
 * has to know which product it is serving just to wrap itself.
 */
import { APP_BAR_LOGOS } from '@components/common/AppBar/logos';
import { useAppMode } from '@variant/useAppMode';

import styles from './LoginShell.module.css';

/**
 * Verbatim from Login.vue:69-77.
 *
 * This is the only place the migration/agreement disclaimer appears in either
 * app, so it travels with the sign-in chrome rather than being dropped as page
 * furniture — and therefore shows on BOTH screens.
 */
const IMPORTANT_NOTICE =
  'Before migrating to an AMD EPYC™ processor-based cloud instance, you must verify ' +
  'that such migration is covered in the agreement between you and your cloud service ' +
  'provider. If AMD-based cloud instances are not covered in your agreement, please ' +
  'contact your cloud provider sales account manager. For further assistance, please ' +
  'contact AMD sales at cloudsales@amd.com.';

/**
 * The product lockup, reusing the app bar's artwork map so there is one place
 * to add a variant's SVG. Falls back to a text lockup for a mode with no asset.
 */
function ProductLogo({ mode, shortName }) {
  const src = APP_BAR_LOGOS[mode];
  const label = `AMD EPYC ${shortName}`;

  return (
    <div className={styles.logo}>
      {src ? (
        <img className={styles.logoImage} src={src} alt={label} />
      ) : (
        <span className={styles.logoFallback}>{label}</span>
      )}
    </div>
  );
}

export function LoginShell({ children }) {
  const { mode, variant } = useAppMode();

  return (
    <div className={styles.page}>
      <section className={styles.panel}>
        <ProductLogo mode={mode} shortName={variant.shortName} />

        <h1 className={styles.title}>Sign-In</h1>
        {/*
          Legacy hard-codes "AMD EPYC Cloud Instance Advisor" here
          (Login.vue:15) even when serving CCA, so CCA users are greeted by the
          wrong product. Read from the variant instead.
        */}
        <p className={styles.subtitle}>Welcome to {variant.productName}</p>

        {children}

        <p className={styles.notice}>
          <strong>Important Notice :</strong> {IMPORTANT_NOTICE}
        </p>
      </section>
    </div>
  );
}
